/**
 * Table text sizes — the Format popup's "Text size" group, shared by every
 * table renderer (Table & Matrix normal / editable / drill-down, the
 * Drill-Down table and the Nested table).
 *
 * All values are px; 0 / absent = that renderer's own default, so a tile
 * that never touched the group looks exactly as before.
 *
 * - `headerFontSize`         every column header
 * - `cellFontSize`           every value cell
 * - `level{n}FontSize`       value cells of rows at level n (1-based), over cellFontSize
 * - `level{n}HeaderFontSize` level n's header (nested table: each level has its own)
 */

/** Deepest level the Format popup offers a size for. */
export const TABLE_TEXT_SIZE_LEVELS = 4;

/** Every key of the group, flat — as the Format popup writes them. */
export const TABLE_TEXT_SIZE_KEYS: readonly string[] = [
  'headerFontSize',
  'cellFontSize',
  ...Array.from({ length: TABLE_TEXT_SIZE_LEVELS }, (_, i) => [`level${i + 1}FontSize`, `level${i + 1}HeaderFontSize`]).flat(),
];

/** A usable px size, or null for "default". */
export function textSizePx(value: unknown): number | null {
  const size = Number(value);
  return Number.isFinite(size) && size > 0 ? size : null;
}

/** Only the keys that are set — to forward onto another renderer's data. */
export function pickTableTextSizes(source: Record<string, unknown> | null | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  if (!source) return out;
  for (const key of TABLE_TEXT_SIZE_KEYS) {
    const size = textSizePx(source[key]);
    if (size !== null) out[key] = size;
  }
  return out;
}

/** Value size for a row at `level` (1-based): that level's size, else the general value size. */
export function levelCellSize(source: Record<string, unknown> | null | undefined, level: number): number | null {
  return textSizePx(source?.[`level${level}FontSize`]) ?? textSizePx(source?.['cellFontSize']);
}

/** Header size for `level` (1-based): that level's header size, else the general header size. */
export function levelHeaderSize(source: Record<string, unknown> | null | undefined, level: number): number | null {
  return textSizePx(source?.[`level${level}HeaderFontSize`]) ?? textSizePx(source?.['headerFontSize']);
}
